import { api } from "./client";
import type {
  CollectionItemKind,
  MaterialAudience,
} from "./collectionTemplates";

// Remediation Tier 2 #11 — admin client for CollectionTemplateBundle.
// Mirrors PhoenixCapital.Api/Controllers/Collection/CollectionTemplateBundlesController.cs
// and the DTOs in CollectionDtos.cs.

export interface CollectionTemplateBundleSlotDto {
  id: string;
  templateId: string;
  templateName: string;
  templateKind: CollectionItemKind;
  templateVersion: number;
  templateActive: boolean;
  order: number;
  isRequired: boolean;
  dueOffsetDays: number | null;
}

export interface CollectionTemplateBundleDto {
  id: string;
  name: string;
  description: string | null;
  audience: MaterialAudience | null;
  audienceTag: string | null;
  active: boolean;
  isDefault: boolean;
  /** Owner Recapitalization Loan (D7, Q8) — true when the list was read for a
   *  deal (`dealId`) and this bundle is that deal type's default, so the
   *  composer pre-checks it. Always false without a deal. */
  suggestedForDeal?: boolean;
  createdAt: string;
  modifiedAt: string;
  slots: CollectionTemplateBundleSlotDto[];
}

export interface SaveCollectionTemplateBundleSlotPayload {
  templateId: string;
  order: number;
  isRequired: boolean;
  dueOffsetDays: number | null;
}

export interface CreateCollectionTemplateBundlePayload {
  name: string;
  description: string | null;
  audience: MaterialAudience | null;
  audienceTag: string | null;
  slots: SaveCollectionTemplateBundleSlotPayload[];
}

export interface UpdateCollectionTemplateBundlePayload
  extends CreateCollectionTemplateBundlePayload {
  active: boolean;
}

const BASE = "/collection/template-bundles";

export const collectionBundlesApi = {
  /** `dealId` (Owner Recapitalization Loan, D7/Q8): when given, the server
   *  hides the recap bundles on an Acquisition deal and flags the recap bundles
   *  `suggestedForDeal` on an Owner Recapitalization deal. Without it the list
   *  is exactly as before. */
  list: (
    includeInactive = false,
    audience: MaterialAudience | null = null,
    dealId: string | null = null,
  ) =>
    api.get<CollectionTemplateBundleDto[]>(BASE, {
      includeInactive,
      audience: audience ?? undefined,
      dealId: dealId ?? undefined,
    }),
  get: (id: string) => api.get<CollectionTemplateBundleDto>(`${BASE}/${id}`),
  create: (body: CreateCollectionTemplateBundlePayload) =>
    api.post<CollectionTemplateBundleDto>(BASE, body),
  update: (id: string, body: UpdateCollectionTemplateBundlePayload) =>
    api.put<CollectionTemplateBundleDto>(`${BASE}/${id}`, body),
  deactivate: (id: string) =>
    api.post<null>(`${BASE}/${id}/deactivate`),
};
